import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { PopupCellParams } from '@models/ag-grid';
import { EditSegmentNameParams, SaveSubtitleResponse, SegmentPopupProps, SystemSegmentPopupProps } from '@models/rate-card/rate-card';
import { BooleanFilter } from '@models/filter-types';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { AddSegmentPopupComponent } from '../../../../../rate-optics/rate-card/segmentation/add-segment-popup/add-segment-popup.component';
import tippy, { hideAll, MultipleTargets } from 'tippy.js';
import { RateCardMetricRowV2 } from '@models/rate-card/rate-card';
import { CONFIRM_DIALOG, ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { EditSegmentPopupComponent } from '../../../../../rate-optics/rate-card/segmentation/edit-segment-popup/edit-segment-popup.component';
import { EditSystemSegmentPopupComponent } from '../../../../../rate-optics/rate-card/segmentation/edit-system-segment-popup/edit-system-segment-popup.component';

@Component({
    selector: 'app-popup-cell',
    templateUrl: './popup-cell.component.html',
    styleUrls: ['./popup-cell.component.scss'],
    standalone: false,
})
export class PopupCellComponent implements ICellRendererAngularComp, AfterViewInit {
    @ViewChild('content', { static: false }) container: ElementRef;

    @ViewChild('trigger') button: { nativeElement: MultipleTargets };

    cdr!: ChangeDetectorRef;

    isOpen = false;
    isPrimaryRate = false;
    isUserGenerated = false;

    params!: PopupCellParams<RateCardMetricRowV2>;

    // @ts-expect-error TS7053: implicit any
    private tippyInstance;

    constructor() {}

    ngAfterViewInit(): void {
        if (this.button?.nativeElement) {
            this.tippyInstance = tippy(this.button.nativeElement);
            this.tippyInstance.disable();
        }
    }

    agInit(params: PopupCellParams<RateCardMetricRowV2>) {
        this.params = params;
        this.setIsPrimaryRate();
        this.isUserGenerated = this.params.isUserGenerated(this.params);
    }

    setIsPrimaryRate() {
        this.isPrimaryRate = this.params?.primaryRateValue?.Value.id === this.params?.data?.metricTypeId;
    }

    refresh() {
        this.setIsPrimaryRate();
        return true;
    }

    onClickHandler() {
        this.togglePopup();
    }

    hideSegment(hideOnOtherRateCard = false) {
        this.params.segmentButtonTrack('Hide Row');
        this.tippyInstance.hide();
        const isDynamic = this.params.isDynamic || (hideOnOtherRateCard && this.params.isPolitical);
        const isPolitical = this.params.isPolitical || (hideOnOtherRateCard && this.params.isDynamic);
        this.params.rateCardSvc
            .addHiddenSegment(this.params.data, this.params.rateCardTab, isDynamic, isPolitical, this.params.isDigital)
            .subscribe((_) => {
                this.params.onRowRemoved(this.params);
            });
    }

    openHideSegmentDialogForConfirmation() {
        const showOtherRateCardToggle = !this.params.isDigital && this.params.isDynamic !== this.params.isPolitical;
        let toggleProperty: BooleanFilter = null;
        if (showOtherRateCardToggle) {
            toggleProperty = new BooleanFilter(this.params.isDynamic ? 'Hide on Political Rate Card' : 'Hide on Dynamic Rate Card')
                .Slider(true)
                .LabelPosition('after');
            toggleProperty.Value = this.params.isDynamic;
        }

        const rowHasPoliticalRate =
            this.params.isPolitical &&
            !this.params.isDynamic &&
            Object.values(this.params.data.value).some((cell) => !cell?.isIssue && !cell?.isPast && cell?.value?.current != null);

        const text = 'Are you sure you want to hide this row from the Rate\n Card? This will hide it for all users';
        const warningText = rowHasPoliticalRate
            ? '\n\nWarning: This row is currently setting a political floor on the Dynamic Rate Card.' +
              ' Hiding it will NOT remove the floor — users will be blocked by a rate they cannot see or edit.'
            : undefined;
        const dialog = this.params.matDialog.open(ConfirmDialogComponent, {
            maxWidth: rowHasPoliticalRate ? '36em' : undefined,
            data: {
                text,
                warningText,
                autoConfirm: false,
                noClickClose: true,
                confirmText: 'Hide Row',
                denyText: 'Cancel',
                denyClose: true,
                toggleProperty,
            },
        });
        dialog.afterClosed().subscribe((result) => {
            if (result === CONFIRM_DIALOG) {
                this.hideSegment(toggleProperty?.Value ?? false);
            }
        });
    }

    openUncombinePopup() {
        this.params.segmentButtonTrack('Uncombine Row');
        this.tippyInstance.hide();
        this.isOpen = false;
        this.params.openUncombineDialog?.(this.params);
    }

    openAddSegmentPopup() {
        this.params.segmentButtonTrack('Add Row');
        this.tippyInstance.hide();
        const channelId = this.params.data.channelId;
        const daypartId = this.params.data.daypartId;
        const spotTypeId = this.params.data.spotTypeId;
        this.params.matDialog.open(AddSegmentPopupComponent, {
            disableClose: true,
            width: '120em',
            data: {
                rateCardTab: this.params.rateCardTab,
                channelGroupId: this.params.channelGroupId,
                channelId,
                spotTypeId,
                daypartId,
                priceFloor: this.params.data.overridePriceFloor,
                priceMax: this.params.data.overridePriceMax,
                primarySpotLength: this.params.primarySpotLength,
                primaryTier: Object.values(this.params.tiers?.[channelId]?.[daypartId]?.[spotTypeId] || {}).find((tier) => tier.isPrimary)
                    ?.displayName,
                isDynamic: this.params.isDynamic,
                isPolitical: this.params.isPolitical,
                isSport: this.params.isSport,
                parentDynamicSegmentation: this.params.data.segmentationIds,
                getRateCardSegments: this.params.getRateCardSegments,
                matDialog: this.params.matDialog,
                appMetricValueIds: this.params.appMetricValueIds(this.params),
                afterSave: (rows: RateCardMetricRowV2[]) => this.params.afterSave(this.params, rows, false),
                reloadParams: {
                    startDate: this.params.lastFilterGetter().startDate,
                    endDate: this.params.lastFilterGetter().endDate,
                    period: this.params.lastFilterGetter().period.id,
                    politicalWindows: this.params.lastFilterGetter().politicalWindows as string[],
                },
                marketId: this.params.data.marketId,
                stationId: this.params.data.stationId,
            } as SegmentPopupProps,
        });
    }

    openEditSegmentPopup() {
        this.params.segmentButtonTrack('Edit Row');
        this.tippyInstance.hide();
        const channelId = this.params.data.channelId;
        const daypartId = this.params.data.daypartId;
        const spotTypeId = this.params.data.spotTypeId;
        if (this.params.data.isMergedSegment && this.params.openEditMergedSegmentDialog) {
            this.params.openEditMergedSegmentDialog(this.params);
            return;
        }
        if (this.isUserGenerated) {
            this.params.matDialog.open(EditSegmentPopupComponent, {
                disableClose: true,
                width: '120em',
                data: {
                    rateCardTab: this.params.rateCardTab,
                    channelGroupId: this.params.channelGroupId,
                    channelId,
                    spotTypeId,
                    daypartId,
                    priceFloor: this.params.data.overridePriceFloor,
                    priceMax: this.params.data.overridePriceMax,
                    primarySpotLength: this.params.primarySpotLength,
                    primaryTier: Object.values(this.params.tiers?.[channelId]?.[daypartId]?.[spotTypeId] || {}).find(
                        (tier) => tier.isPrimary,
                    )?.displayName,
                    isDynamic: this.params.isDynamic,
                    isPolitical: this.params.isPolitical,
                    getRateCardSegments: this.params.getRateCardSegments,
                    matDialog: this.params.matDialog,
                    appMetricValueIds: this.params.appMetricValueIds(this.params),
                    afterSave: (rows: RateCardMetricRowV2[]) => this.params.afterSave(this.params, rows, true),
                    reloadParams: {
                        startDate: this.params.lastFilterGetter().startDate,
                        endDate: this.params.lastFilterGetter().endDate,
                        period: this.params.lastFilterGetter().period.id,
                        politicalWindows: this.params.lastFilterGetter().politicalWindows as string[],
                    },
                    marketId: this.params.data.marketId,
                    stationId: this.params.data.stationId,
                } as SegmentPopupProps,
            });
        } else {
            this.params.matDialog.open(EditSystemSegmentPopupComponent, {
                disableClose: true,
                data: {
                    channelId,
                    daypartId: this.params.data.daypartId,
                    daypart: this.params.data.daypart,
                    segmentation: this.params.data.segmentationIds,
                    periodStartDate: this.params.data.periodStartDate,
                    subtitle: this.params.data.subtitle,
                    afterSave: (
                        segmentParams: EditSegmentNameParams[],
                        reloadRows: RateCardMetricRowV2[],
                        subtitle?: SaveSubtitleResponse,
                    ) => this.params.editSegmentName(segmentParams, reloadRows, subtitle),
                    getRateCardSegments: this.params.getRateCardSegments,
                    matDialog: this.params.matDialog,
                    baseSegmentationId: this.params.data.rateCardIdInt,
                    spotTypeId: this.params.data.spotTypeId,
                    reloadParams: {
                        startDate: this.params.lastFilterGetter().startDate,
                        endDate: this.params.lastFilterGetter().endDate,
                        period: this.params.lastFilterGetter().period.id,
                    },
                    rateCardTab: this.params.rateCardTab,
                    isPolitical: this.params.isPolitical,
                } as SystemSegmentPopupProps,
            });
        }
    }

    configureTippyInstance() {
        this.tippyInstance.enable();
        this.tippyInstance.show();

        this.tippyInstance.setProps({
            trigger: 'manual',
            placement: 'right',
            arrow: false,
            interactive: true,
            appendTo: document.body,
            hideOnClick: false,
            // @ts-expect-error TS7053: implicit any
            onShow: (instance) => {
                hideAll({ exclude: instance });
            },
            // @ts-expect-error TS7053: implicit any
            onClickOutside: (instance) => {
                this.isOpen = false;
                instance.unmount();
            },
        });
    }

    togglePopup() {
        this.isOpen = !this.isOpen;
        setTimeout(() => {
            if (this.isOpen) {
                this.configureTippyInstance();
                this.tippyInstance.setContent(this.container.nativeElement);
            } else {
                this.tippyInstance.unmount();
            }
        });
    }
}
